import React, { useState, useEffect } from "react";
import styled from "styled-components";
import image from "../assets/adopt_tree_buddy.jpg";
import Header from "../styles/Header";
import { useNavigate } from "react-router";
import { motion } from "framer-motion";
import { FaCalendarAlt, FaUsers, FaClock, FaTree, FaDollarSign } from "react-icons/fa";
import { collection, getDocs, orderBy, query, addDoc, onSnapshot } from "firebase/firestore";
import { db, auth } from "../authentication/firebase";
import { CircularProgress } from "@mui/material";
import WaitlistPopup from "../components/modals/WaitlistPopup";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contributionLoading, setContributionLoading] = useState(null);
  const [showWaitlistPopup, setShowWaitlistPopup] = useState(false);

  // Function to fetch projects from Firebase
  const fetchProjectsFromFirebase = async () => {
    try {
      setLoading(true);

      const projectsCollectionRef = collection(db, "projects");
      const projectsQuery = query(projectsCollectionRef, orderBy("order", "asc"));
      const projectsSnapshot = await getDocs(projectsQuery);

      const fetchedProjects = projectsSnapshot.docs.map(doc => ({
        firebaseId: doc.id,
        ...doc.data()
      }));

      setProjects(fetchedProjects);

    } catch (error) {
      console.error("Error fetching projects from Firebase:", error);
      setError("Failed to load projects. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectsFromFirebase();
  }, []);

  // Handle project contribution
  const handleProjectContribution = async (project) => {
    // Check if project has a payment link first
    if (project.paymentLink) {
      // Open the payment link directly without authentication check
      window.open(project.paymentLink, '_blank');
      return;
    }

    // Only check authentication for the old checkout session method
    const uid = auth.currentUser?.uid;
    if (!uid) {
      navigate("/login", { state: { from: "/projects" } });
      return;
    }

    // Fallback to the old checkout session method if no payment link
    setContributionLoading(project.firebaseId);
    try {
      const collectionRef = collection(
        db,
        "customers",
        uid,
        "checkout_sessions"
      );

      const docRef = await addDoc(collectionRef, {
        mode: "payment",
        payment_method_types: ["card"],
        line_items: [
          {
            price_data: {
              currency: "usd",
              product_data: {
                name: `Contribution to: ${project.title}`,
                description: `Support ${project.title} - Help us reach our $${project.budget?.toLocaleString()} goal`,
                images: project.img ? [project.img] : [],
              },
              unit_amount: 1000, // $10 minimum contribution (in cents)
            },
            quantity: 1,
          },
        ],
        metadata: {
          projectId: project.firebaseId,
          contributionType: "project_funding",
        },
        success_url: `${window.location.origin}/payment-successful?project=${project.firebaseId}`,
        cancel_url: `${window.location.origin}/projects?error=true`,
      });

      onSnapshot(docRef, (snap) => {
        const { error, url } = snap?.data();
        if (error) {
          console.error("Error creating checkout session:", error);
          setContributionLoading(null);
        } else if (url) {
          window.location.assign(url);
        }
      });
    } catch (e) {
      setContributionLoading(null);
      console.error("Error while creating project contribution checkout:", e);
    }
  };

  return (
    <>
      <Header
        title="Earth in Motion"
        description="Act locally, restore globally. Every project you engage with addresses a unique piece of the climate and biodiversity challenge. The path forward starts from the ground up."
      />

      {loading ? (
        <LoadingContainer>
          <CircularProgress color="inherit" size={50} />
          <LoadingText>Loading projects...</LoadingText>
        </LoadingContainer>
      ) : error ? (
        <ErrorContainer>
          <p>{error}</p>
          <button onClick={fetchProjectsFromFirebase} className="primary-btn">
            Try Again
          </button>
        </ErrorContainer>
      ) : (
        <ProjectsWrapper>
          {/* Featured Project Section - Modern Unified Design */}
          <FeaturedSection
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <FeaturedContent>
              <TextSection variants={itemVariants}>
                <FeatureBadge>Featured Initiative</FeatureBadge>
                <h2>Adopt a Living Tree</h2>
                <p>
                  Wroot will plant a unique tree on our behalf. We will take care of it every step of the way. From soil to sunlight, our team ensures your tree thrives in one of our dedicated restoration sites. You'll receive regular updates, photos and progress reports so you can watch your tree grow overtime.
                </p>
                <p>
                  You will be able to assign a name for your tree friend. And you will receive the exact coordinates of your friends location, in case you want to come by and say hello
                </p>
                <motion.button
                  className="primary-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowWaitlistPopup(true)}
                >
                  Get Started
                </motion.button>
              </TextSection>
              <ImageSection variants={itemVariants}>
                <img src={image} alt="Adopt Tree" />
              </ImageSection>
            </FeaturedContent>
          </FeaturedSection>

          {/* Projects Showcase */}
          <ProjectsShowcase
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <SectionHeading variants={itemVariants}>
              <h2>Our Active Projects</h2>
              <p>Join us in our mission to restore nature and combat climate change through these impactful initiatives</p>
            </SectionHeading>

            {projects.length === 0 ? (
              <NoProjectsMessage>
                No active projects found. Check back later for new initiatives!
              </NoProjectsMessage>
            ) : (
              projects.map((project, index) => (
                <ProjectCard
                  key={project.firebaseId || index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={containerVariants}
                >
                  <ProjectImageWrapper variants={itemVariants}>
                    <img src={project.img} alt={project.title} />
                  </ProjectImageWrapper>

                  <ProjectDetails variants={itemVariants}>
                    <ProjectHeader>
                      <h3>{project.title}</h3>
                    </ProjectHeader>

                    {/* Unified Project Info Section */}
                    <ProjectInfoSection>
                      {/* Project Stats */}
                      <ProjectStats>
                        <StatItem>
                          <FaCalendarAlt />
                          <div>
                            <p>Start Date</p>
                            <h5>{project.startDate}</h5>
                          </div>
                        </StatItem>
                        <StatItem>
                          <FaUsers />
                          <div>
                            <p>Contributors</p>
                            <h5>{project.contributors}</h5>
                          </div>
                        </StatItem>
                        <StatItem>
                          <FaClock />
                          <div>
                            <p>Timeline</p>
                            <h5>{project.timeline}</h5>
                          </div>
                        </StatItem>
                        <StatItem>
                          <FaTree />
                          <div>
                            <p>Area</p>
                            <h5>{project.landSize}</h5>
                          </div>
                        </StatItem>
                      </ProjectStats>

                      {/* Integrated Funding Progress */}
                      <FundingProgressSection>
                        <FundingTopRow>
                          <FundingTitle>
                            <FaDollarSign />
                            <span>Project Funding</span>
                          </FundingTitle>
                          <FundingAmount>
                            ${(project.contributedAmount || 0).toLocaleString()} / ${(project.budget || 0).toLocaleString()}
                          </FundingAmount>
                        </FundingTopRow>
                        
                        <ProgressBarContainer>
                          <ProgressBar
                            progress={((project.contributedAmount || 0) / (project.budget || 1)) * 100}
                          />
                          <ProgressMeta>
                            <ProgressText>
                              {Math.round(((project.contributedAmount || 0) / (project.budget || 1)) * 100)}% funded
                            </ProgressText>
                            <ProgressTarget>
                              Goal: ${(project.budget || 0).toLocaleString()}
                            </ProgressTarget>
                          </ProgressMeta>
                        </ProgressBarContainer>
                      </FundingProgressSection>
                    </ProjectInfoSection>

                    <ProjectDescription>
                      {project.description}
                    </ProjectDescription>

                    <ActionButtons>
                      <motion.button
                        className="primary-btn"
                        onClick={() => handleProjectContribution(project)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={contributionLoading === project.firebaseId}
                      >
                        {contributionLoading === project.firebaseId ? (
                          <>
                            <CircularProgress size={16} color="inherit" style={{ marginRight: '8px' }} />
                            Processing...
                          </>
                        ) : (
                          'Make it happen'
                        )}
                      </motion.button>
                    </ActionButtons>
                  </ProjectDetails>
                </ProjectCard>
              ))
            )}
          </ProjectsShowcase>
        </ProjectsWrapper>
      )}
      
      <WaitlistPopup 
        isOpen={showWaitlistPopup}
        onClose={() => setShowWaitlistPopup(false)}
      />
    </>
  );
};

export default Projects;

// Styled Components
const ProjectsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: var(--section-width);
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 0;
  gap: 20px;
  width: var(--section-width);
  margin: 0 auto;
`;

const LoadingText = styled.p`
  color: var(--text-color);
  font-size: 1.1rem;
  margin: 0;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 0;
  gap: 20px;
  width: var(--section-width);
  margin: 0 auto;
  text-align: center;
  
  p {
    color: var(--text-color);
    font-size: 1.1rem;
    margin: 0;
  }
  
  button {
    padding: 12px 24px;
    border-radius: 50px;
    border: none;
    background: var(--primary-color);
    color: var(--text-color);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const NoProjectsMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-gray-color);
  font-size: 1.2rem;
  background: var(--section-bg-color);
  border-radius: 12px;
  margin: 20px 0;
`;

// Modern Featured Section - Unified Container
const FeaturedSection = styled(motion.section)`
  margin-bottom: 80px;
  position: relative;
  z-index: 1;
`;

const FeaturedContent = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
  background: white;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
    padding: 30px;
  }
`;

const TextSection = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  h2 {
    margin: 16px 0 20px 0;
    font-size: 2.5rem;
    color: var(--text-light-color);
    line-height: 1.2;
  }
  
  p {
    margin-bottom: 18px;
    line-height: 1.8;
    color: black;
    font-size: 1rem;
  }
  
  button {
    align-self: flex-start;
    margin-top: 20px;
    padding: 14px 28px;
    font-weight: 600;
    font-size: 1rem;
  }
  
  @media (max-width: 768px) {
    text-align: center;
    
    button {
      align-self: center;
    }
  }
`;

const ImageSection = styled(motion.div)`
  flex: 1;
  height: 400px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    filter: brightness(1.1) contrast(1.15) saturate(1.2);
    border-radius: 16px;
  }
  
  @media (max-width: 768px) {
    height: 350px;
  }
  
  @media (max-width: 480px) {
    height: 300px;
  }
`;


const FeatureBadge = styled.span`
  display: inline-block;
  padding: 8px 16px;
  background-color: var(--primary-color);
  color: var(--text-color);
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 30px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 10px rgba(185, 226, 23, 0.3);
`;

// Project Cards Styling
const ProjectsShowcase = styled(motion.section)`
  display: flex;
  flex-direction: column;
  gap: 60px;
`;

const SectionHeading = styled(motion.div)`
  text-align: center;
  margin-bottom: 20px;
  
  h2 {
    color: var(--text-light-color);
    font-size: 2.2rem;
    margin-bottom: 16px;
  }
  
  p {
    max-width: 700px;
    margin: 0 auto;
    color: var(--text-gray-color);
  }
`;

const ProjectCard = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  background: white;
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(185, 226, 23, 0.1);
  
  &:hover {
    box-shadow: 
      0 20px 50px rgba(0, 0, 0, 0.12),
      0 0 0 2px rgba(185, 226, 23, 0.2);
    border-color: var(--primary-color);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--primary-color) 0%, #8bc34a 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: 10;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 0;
  }
`;

const ProjectImageWrapper = styled(motion.div)`
  flex: 1;
  min-width: 300px;
  min-height: 350px;
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    filter: brightness(1.02) saturate(1.1);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(185, 226, 23, 0.05) 0%,
      rgba(139, 195, 74, 0.03) 50%,
      rgba(76, 175, 80, 0.02) 100%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  
  ${ProjectCard}:hover & img {
    filter: brightness(1.05) saturate(1.2);
  }
  
  ${ProjectCard}:hover &::after {
    opacity: 1;
  }
  
  @media (max-width: 1024px) {
    min-height: 280px;
  }
`;

const ProjectDetails = styled(motion.div)`
  flex: 1.5;
  min-width: 300px;
  padding: 45px;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #ffffff 0%, #fafffe 100%);
  
  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 2px solid rgba(185, 226, 23, 0.08);
  
  h3 {
    color: var(--text-light-color);
    margin: 0;
    font-size: 1.85rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.3;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
    padding-bottom: 16px;
    
    h3 {
      font-size: 1.5rem;
      line-height: 1.4;
    }
  }
`;


// Unified Project Info Container
const ProjectInfoSection = styled.div`
  margin-bottom: 36px;
  background: linear-gradient(135deg, #fafffe 0%, #f8fcff 100%);
  border-radius: 20px;
  padding: 32px;
  border: 1px solid rgba(185, 226, 23, 0.15);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
  position: relative;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 70% 30%, rgba(185, 226, 23, 0.03) 0%, transparent 50%);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 
      0 16px 48px rgba(0, 0, 0, 0.08),
      0 0 0 1px rgba(185, 226, 23, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    border-color: rgba(185, 226, 23, 0.25);
  }
  
  @media (max-width: 768px) {
    padding: 20px 12px;
    margin-bottom: 28px;
    border-radius: 16px;
    margin-left: -4px;
    margin-right: -4px;
  }
`;

const ProjectStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 24px;
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 2px solid rgba(185, 226, 23, 0.12);
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 24px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.6) 0%, rgba(248, 252, 255, 0.4) 100%);
  border-radius: 12px;
  border: 1px solid rgba(185, 226, 23, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
    border-color: rgba(185, 226, 23, 0.15);
  }
  
  svg {
    color: var(--primary-color);
    font-size: 1.4rem;
    margin-top: 2px;
    flex-shrink: 0;
    filter: drop-shadow(0 2px 4px rgba(185, 226, 23, 0.2));
  }
  
  div {
    display: flex;
    flex-direction: column;
    
    p {
      font-size: 0.78rem;
      color: var(--text-gray-color);
      margin: 0;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    h5 {
      font-size: 1.1rem;
      color: var(--text-color);
      margin: 6px 0 0;
      font-weight: 700;
      letter-spacing: -0.01em;
    }
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    gap: 12px;
    
    svg {
      font-size: 1.2rem;
    }
    
    div h5 {
      font-size: 1rem;
    }
  }
`;

// New Funding Progress Section
const FundingProgressSection = styled.div`
  padding-top: 4px;
`;

const FundingTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const FundingTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: var(--primary-color);
    font-size: 1.3rem;
  }
  
  span {
    font-size: 1.1rem;
    color: var(--text-color);
    font-weight: 700;
  }
`;

const FundingAmount = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: #2d5016;
  background: linear-gradient(135deg, rgba(185, 226, 23, 0.12) 0%, rgba(139, 195, 74, 0.08) 100%);
  padding: 10px 16px;
  border-radius: 25px;
  border: 1px solid rgba(185, 226, 23, 0.25);
  box-shadow: 0 2px 8px rgba(185, 226, 23, 0.1);

  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 8px 14px;
  }
`;

const ProgressBarContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: linear-gradient(90deg, #e8f5e8 0%, #f0f8ff 100%);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  box-shadow: 
    inset 0 2px 4px rgba(0, 0, 0, 0.06),
    0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(139, 195, 74, 0.2);
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => Math.min(props.progress, 100)}%;
    background: linear-gradient(135deg, #b9e217 0%, #8bc34a 50%, #689f38 100%);
    border-radius: 7px;
    transition: width 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    box-shadow: 
      0 2px 8px rgba(139, 195, 74, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    position: relative;
    
    ${props => props.progress > 0 && `
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%);
        border-radius: 7px;
        animation: shimmer-enhanced 2s infinite;
        opacity: 0.9;
      }
    `}
  }
  
  @keyframes shimmer-enhanced {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const ProgressMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const ProgressText = styled.span`
  font-size: 0.95rem;
  color: #2d5016;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &::before {
    content: '🌱';
    font-size: 0.9rem;
  }
`;

const ProgressTarget = styled.span`
  font-size: 0.85rem;
  color: var(--text-gray-color);
  font-weight: 500;
  background-color: rgba(139, 195, 74, 0.08);
  padding: 4px 8px;
  border-radius: 12px;
`;

const ProjectDescription = styled.p`
  line-height: 1.7;
  margin-bottom: 30px;
  color: var(--text-gray-color);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-top: auto;
  padding-top: 24px;
  
  button {
    padding: 16px 32px;
    font-weight: 600;
    border-radius: 12px;
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    width: 100%;
    justify-content: center;
    
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none !important;
    }
  }
  
  .primary-btn {
    box-shadow: 0 6px 20px rgba(185, 226, 23, 0.3);
    
    &:hover {
      box-shadow: 0 8px 28px rgba(185, 226, 23, 0.4);
    }
  }
  
  @media (max-width: 480px) {
    padding-top: 20px;
  }
`;